import SinglePost from './SinglePost'
import LightboxModal from './Elements/Modal';
import SwipeView from './Elements/SwipeView';
import useSwipeViewer from '../../../Frontend/Hooks/useSwipeViewer';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const GridLayout = ({ feedSettings, headerData, posts, isPro, editorActiveDevice, isFeedEditor }) => {
    const [activePost, setActivePost] = useState(null);
    const [activeIndex, setActiveIndex] = useState(null);
    const [isLightboxVisible, setIsLightboxVisible] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    // SMASH-1377 v3-r11: the lightbox is conditionally mounted and opened by a
    // plain <button onClick> (not a Radix DialogTrigger), so on close DialogRoot
    // unmounts with no trigger ref for FocusScope to restore to and focus drops
    // to <body> (WCAG 2.4.3). Capture the triggering post button on open and
    // re-focus it once the lightbox has unmounted.
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!isLightboxVisible && triggerRef.current) {
            triggerRef.current.focus();
            triggerRef.current = null;
        }
    }, [isLightboxVisible]);

    let desktopNumber = feedSettings.gridDesktopColumns;

    if (isFeedEditor) {
        switch (editorActiveDevice.device) {
            case 'mobile':
                desktopNumber = feedSettings.gridMobileColumns;
                break;
            case 'tablet':
                desktopNumber = feedSettings.gridTabletColumns;
                break;
            default:
                desktopNumber = feedSettings.gridDesktopColumns;
                break;
        }
    }

    // SMASH-1853: Swipe View (Pro-gated, spec §2.1/§10) intercepts the click
    // when the post is video-eligible; every other post keeps this exact
    // lightbox path unchanged (spec §2.2 — non-eligible activation MUST open
    // the existing lightbox).
    const swipe = useSwipeViewer({ isPro, feedSettings });

    //
    const handleModalOpen = (postIndex, triggerEl) => {
        triggerRef.current = triggerEl || null;
        setActivePost(posts[postIndex]);
        setActiveIndex(postIndex);
        setIsLightboxVisible(true);
    }

    //
    const handlePostActivate = (postIndex, triggerEl) => {
        swipe.handlePostActivate(posts, postIndex, triggerEl, handleModalOpen);
    }

    //
    const handleCloseModal = () => {
        setActivePost(null);
        setIsLightboxVisible(false);
        document.body.classList.remove('sbt-lightbox-modal-open');
    }

    //
    const handlePreviousItem = () => {
        const currentPostIndex = posts.findIndex(post => post.id === activePost.id);
        if (currentPostIndex === 0) {
            //
        }
        else {
            setActivePost(posts[currentPostIndex - 1]);
            setActiveIndex(currentPostIndex - 1);
        }
    }

    //
    const handleNextItem = () => {
        const currentPostIndex = posts.findIndex(post => post.id === activePost.id);
        if (currentPostIndex === posts.length - 1) {
            //
        }
        else {
            setActivePost(posts[currentPostIndex + 1]);
            setActiveIndex(currentPostIndex + 1);
        }
    }


    const columnGap = `${ feedSettings.horizontalSpacing === "" || feedSettings.horizontalSpacing == null ? 16 : feedSettings.horizontalSpacing }px`;
    const rowGap = `${ feedSettings.verticalSpacing === "" || feedSettings.verticalSpacing == null ? 16 : feedSettings.verticalSpacing }px`;

    return (
        <>
            <div
                className='sb-grid-wrapper sb-fs'
                role="list"
                data-grid-columns={desktopNumber}
                data-grid-tablet-columns={feedSettings.gridTabletColumns}
                data-grid-mobile-columns={feedSettings.gridMobileColumns}
                style={{
                    display: 'grid',
                    columnGap,
                    rowGap
                }}
            >
                {
                    posts.map((post, postIndex) =>
                        <SinglePost
                            headerData={headerData}
                            post={post}
                            postIndex={postIndex}
                            feedSettings={feedSettings}
                            key={postIndex}
                            isPro={isPro}
                            isFeedEditor={isFeedEditor}
                            handlePostClick={handlePostActivate}
                            isPlaying={isPlaying}
                            setIsPlaying={setIsPlaying}
                            activeIndex={activeIndex}
                            setActiveIndex={setActiveIndex}
                            isListItem={true}
                        />
                    )
                }
            </div>

            {isLightboxVisible && activePost &&
                createPortal(
                    <LightboxModal closeModal={handleCloseModal}
                        post={activePost}
                        headerData={headerData}
                        feedSettings={feedSettings}
                        handleNext={handleNextItem}
                        handlePrev={handlePreviousItem}
                        showNext={
                            !(activeIndex === posts.length - 1)
                        }
                        showPrev={
                            !(activeIndex === 0)
                        }
                        isPro={isPro}
                        isLightboxVisible={isLightboxVisible}
                        setIsLightboxVisible={setIsLightboxVisible}
                    />,
                    document.body
                )
            }

            {swipe.isSwipeOpen &&
                createPortal(
                    <SwipeView
                        posts={swipe.swipeTrack}
                        startIndex={swipe.swipeStartIndex}
                        headerData={headerData}
                        counts={swipe.counts}
                        opener={swipe.opener}
                        onClose={swipe.closeSwipeView}
                    />,
                    document.body
                )
            }
        </>
    )
}

export default GridLayout;